import { normalizeWerkplanScope } from "@plaatplan/core";
import { useLocale } from "../i18n/useLocale";
import { summarizeMaterialPlan } from "./materialLabels";

function ScopeToggle({ selected, title, detail, onClick, disabled }) {
  return (
    <button
      type="button"
      className={`werkplan-scope-toggle${selected ? " selected" : ""}`}
      aria-pressed={selected}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="werkplan-scope-toggle-check" aria-hidden="true">{selected ? "✓" : ""}</span>
      <strong>{title}</strong>
      <small>{detail}</small>
    </button>
  );
}

export function WerkplanMaterialScope({
  werkplan,
  layout,
  materialName,
  sourcePanel,
  onChange,
}) {
  const { display } = useLocale();
  const { includeNewSheets, includeRestSheets } = normalizeWerkplanScope(werkplan);
  const newSheetCount = layout.metrics?.newSheetCount ?? 0;
  const restSheetCount = layout.metrics?.restSheetCount ?? 0;
  const materialLabel = summarizeMaterialPlan(materialName, layout.breakdown);
  const sheetSize = display.formatSheet(sourcePanel.length, sourcePanel.width);

  const toggleScope = (field) => {
    const isOn = werkplan[field] !== false;
    const otherField = field === "includeNewSheets" ? "includeRestSheets" : "includeNewSheets";
    const otherOn = werkplan[otherField] !== false;
    if (isOn && !otherOn) return;
    onChange({ ...werkplan, [field]: !isOn });
  };

  const onlyNew = includeNewSheets && !includeRestSheets;
  const onlyRest = includeRestSheets && !includeNewSheets;

  const newSheetsDetail = newSheetCount > 0
    ? display.t(newSheetCount === 1
      ? "web.werkplan.materialScope.newSheetsDetail"
      : "web.werkplan.materialScope.newSheetsDetailPlural", {
      count: newSheetCount,
      size: sheetSize,
    })
    : display.t("web.werkplan.materialScope.newSheetsNone", { size: sheetSize });

  const restDetail = restSheetCount > 0
    ? display.t(restSheetCount === 1
      ? "web.werkplan.materialScope.restDetail"
      : "web.werkplan.materialScope.restDetailPlural", {
      count: restSheetCount,
    })
    : display.t("web.werkplan.materialScope.restNone");

  return (
    <section className="export-section werkplan-material-scope no-print">
      <div className="werkplan-material-scope-head">
        <div>
          <h2>{display.t("web.werkplan.materialScope.title")}</h2>
          <p className="export-note">
            {display.t("web.werkplan.materialScope.intro", { material: materialLabel })}
          </p>
        </div>
      </div>

      <div
        className="werkplan-scope-toggles"
        role="group"
        aria-label={display.t("web.werkplan.materialScope.groupLabel")}
      >
        <ScopeToggle
          selected={includeNewSheets}
          title={display.t("web.werkplan.materialScope.newSheets")}
          detail={newSheetsDetail}
          onClick={() => toggleScope("includeNewSheets")}
          disabled={includeNewSheets && !includeRestSheets}
        />
        <ScopeToggle
          selected={includeRestSheets}
          title={display.t("web.werkplan.materialScope.restStock")}
          detail={restDetail}
          onClick={() => toggleScope("includeRestSheets")}
          disabled={includeRestSheets && !includeNewSheets}
        />
      </div>

      {onlyNew && restSheetCount > 0 && (
        <p className="werkplan-scope-hint">
          {display.t("web.werkplan.materialScope.onlyNewHint")}
        </p>
      )}
      {onlyRest && newSheetCount > 0 && (
        <p className="werkplan-scope-hint">
          {display.t("web.werkplan.materialScope.onlyRestHint")}
        </p>
      )}
      {!includeNewSheets && !includeRestSheets && (
        <p className="werkplan-scope-hint warning">
          {display.t("web.werkplan.materialScope.noneSelected")}
        </p>
      )}
    </section>
  );
}
